import { computed, ref, Ref, watch } from "vue";
import { ExponentialBackoff, Websocket, WebsocketEvent, Backoff } from "websocket-ts";
import { PingRequest, Request } from "@/websocket/model/messages/requests";
import { Response, PongResponse, PubMsgResponse } from "@/websocket/model/messages/responses";
import { MessageType } from "@/websocket/model/enums/message-type";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import useStopwatch from "@/shared/composables/use-stopwatch";
import usePubMsgResponseMapper from "@/websocket/composables/use-pub-msg-response-mapper";
import { Channel } from "@/websocket/model/channel";
import { ChannelType } from "@/websocket/model/enums/channel-type";
import { useAuthStore } from "@/shared/stores/use-auth-store";
import useOpenbetWsClient from "@/websocket/composables/use-openbet-ws-client";
import { wait, waitUntilTrue } from "@/shared/utils/wait";

// Turn off for production server and keep only for development on localhost
// const extendedDebugging = isDevelopmentEnv();
const extendedDebugging = false; // cluttering console with too much info

export default function useOpenbetWs() {
    const {
        getWsClient,
        openConnection,
        closeConnection,
        resetConnection,
        setToken,
        sendRequest,
        sendMessage,
        sendInitialConnectionMessages,
        subscribeChannels,
        unsubscribeChannels,
        subscribeChannelsByType,
        unsubscribeChannelsByType,
    } = useOpenbetWsClient();
    const authStore = useAuthStore();

    // Default range for ping id
    const PING_ID: { readonly min: number; readonly max: number } = {
        min: 0,
        max: 9999,
    };

    const token = computed(() =>
        authStore.wsToken?.token
            ? `${authStore.wsToken.expiration_date}:${authStore.wsToken.channel}:${authStore.wsToken.token}`
            : "",
    );

    // Ping & connection health - start
    const pingId: Ref<number> = ref(PING_ID.min);
    const pingTimeout = 20000; // Time to wait for a pong to be received after sending a ping before assuming something is wrong
    const pingIntervalId: Ref<number> = ref();
    const pingIntervalLength = 15000; // How often should pings be sent... Timeout set to 15s even though in documentation is 30s which is too high and happens that connection is closed prematurely
    const connectionEstablished = ref(false);
    const backoff: Ref<Backoff> = ref(new ExponentialBackoff(1000, 10)); // retry every 1s, 2s, 4s, 8s, 16s, 32s, 64s...

    const waitUntilConnectionEstablished = async (time = 100) => {
        await waitUntilTrue(connectionEstablished, time);
    };

    const stopwatch = useStopwatch(pingTimeout);

    const sendPing = () => {
        if (pingId.value === PING_ID.max) pingId.value = PING_ID.min; // Reset counter if it reaches its end
        pingId.value = pingId.value + 1;
        const req = new PingRequest(pingId.value);
        sendRequest(req);
        stopwatch.start();
    };

    const startPing = () => {
        if (extendedDebugging) console.log("ping started");
        pingIntervalId.value = setInterval(() => sendPing(), pingIntervalLength);
    };

    const stopPing = () => {
        clearInterval(pingIntervalId.value);
        if (extendedDebugging) console.log("ping stopped");
        pingId.value = PING_ID.min;
    };

    watch(stopwatch.alarm, (newValue) => {
        if (newValue) {
            // Reset the connection if ping timeout expires
            console.warn(`ping (${pingId.value}) breached ${pingTimeout}ms timeout!!!`, stopwatch.elapsed() + "ms");
            closeConnection();
        }
    });
    // If token changes we need to reinit the connection
    watch(token, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            if (extendedDebugging)
                console.log(`ws connection reinitialisation with token '${newValue}' in progress...`);
            setToken(newValue);
            closeConnection();
        }
    });

    watch(connectionEstablished, async (newValue) => {
        if (newValue) {
            if (extendedDebugging) console.log("ws connection established");
            sendInitialConnectionMessages();
            startPing();
            backoff.value.reset();
        } else {
            if (extendedDebugging) console.log("ws connection reset in progress...");
            stopPing();
            await wait(backoff.value.current);
            if (extendedDebugging) console.log("ws connection - current backoff value", backoff.value.current);
            resetConnection(initListeners);
            backoff.value.next();
        }
    });

    const initConnectionListeners = (ws: Websocket) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onOpen = (_i: Websocket, _ev: Event) => {
            connectionEstablished.value = true;
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onClose = (_i: Websocket, _ev: CloseEvent) => {
            connectionEstablished.value = false;
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onError = (_i: Websocket, ev: Event) => {
            if (extendedDebugging) console.log("ws connection error:", ev);
            connectionEstablished.value = false;
        };

        // Add event listeners
        ws.addEventListener(WebsocketEvent.open, onOpen);
        ws.addEventListener(WebsocketEvent.close, onClose);
        ws.addEventListener(WebsocketEvent.error, onError);
    };
    // Ping & connection health - end

    // Message catching - start
    const { mapToStore } = usePubMsgResponseMapper();
    const initMessageListeners = (ws: Websocket) => {
        const onMessage = (_i: Websocket, ev: MessageEvent) => {
            const response = Response.parse(ev.data);
            switch (response.type) {
                case MessageType.Pong: {
                    stopwatch.stop();
                    const pongResponse = response as PongResponse;
                    if (pingId.value !== pongResponse.id) {
                        // Reset the connection if ping and pong id does not match
                        console.warn(`pong (${pongResponse.id}) does not match ping (${pingId.value})!!!`);
                        closeConnection();
                    }
                    break;
                }
                case MessageType.PubMsg:
                default: {
                    const pubMsgResponse = response as PubMsgResponse;
                    mapToStore(pubMsgResponse);
                }
            }
        };

        ws.addEventListener(WebsocketEvent.message, onMessage);
    };
    // Message catching - end

    // Add the whole logic around ping, connection health and message catching
    const initListeners = (ws: Websocket) => {
        initConnectionListeners(ws);
        initMessageListeners(ws);
    };
    if (!getWsClient()) {
        setToken(token.value);
        openConnection(initListeners);
    }

    const _sendRequest = async (req: Request, buffer?: boolean) => {
        await waitUntilConnectionEstablished();
        sendRequest(req, buffer);
    };

    const _sendMessage = async (msg: string, buffer?: boolean) => {
        await waitUntilConnectionEstablished();
        sendMessage(msg, buffer);
    };

    const _subscribeChannels = async (channels: Channel[]) => {
        await waitUntilConnectionEstablished();
        subscribeChannels(channels);
    };

    const _unsubscribeChannels = async (channels: Channel[]) => {
        await waitUntilConnectionEstablished();
        unsubscribeChannels(channels);
    };

    const _subscribeChannelsByType = async (channelType: ChannelType, ids: number[]) => {
        await waitUntilConnectionEstablished();
        subscribeChannelsByType(channelType, ids);
    };

    const _unsubscribeChannelsByType = async (channelType: ChannelType, ids: number[]) => {
        await waitUntilConnectionEstablished();
        unsubscribeChannelsByType(channelType, ids);
    };

    return {
        waitUntilConnectionEstablished,
        sendRequest: _sendRequest,
        sendMessage: _sendMessage,
        subscribeChannels: _subscribeChannels,
        unsubscribeChannels: _unsubscribeChannels,
        subscribeChannelsByType: _subscribeChannelsByType,
        unsubscribeChannelsByType: _unsubscribeChannelsByType,
    };
}
